/**
 * Rotating QR tokens shared by the customer app, the merchant scanner and the API (docs/3-backend.md §3.5).
 *   token = PREFIX "." base64url(id 16 bytes) "." window "." base32(HMAC-SHA256(secret, "PREFIX|id|window")[0..10))
 * Uses Web Crypto only, so the same code runs in browsers and in Node 22. Depends on nothing internal.
 */

export const WINDOW_MS = 30_000;
/** Accepted windows: now−2 … now+1 (≈ 60 s of clock skew). */
export const SKEW_PAST = 2;
export const SKEW_FUTURE = 1;
const MAC_BYTES = 10;

export type TokenPrefix = 'IR1' | 'IRV1';

export type VerifyResult =
  | { ok: true; id: string; window: number }
  | { ok: false; reason: 'INVALID' | 'EXPIRED' };

const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const encoder = new TextEncoder();

/** Window number of an instant, corrected by the device's measured offset to server time. */
export function windowAt(epochMillis: number, serverOffsetMillis = 0): number {
  return Math.floor((epochMillis + serverOffsetMillis) / WINDOW_MS);
}

/** Milliseconds until the next window starts, on the device clock (the QR redraws exactly then). */
export function msLeftInWindow(epochMillis: number, serverOffsetMillis = 0): number {
  const into = (((epochMillis + serverOffsetMillis) % WINDOW_MS) + WINDOW_MS) % WINDOW_MS;
  return WINDOW_MS - into;
}

/** Server time minus device time, taken at the middle of the request (the server answered somewhere in it). */
export function measureServerOffset(serverTime: number, sentAt: number, receivedAt: number): number {
  return Math.round(serverTime - (sentAt + receivedAt) / 2);
}

/** FR-QR-04: beyond this the device clock is probably wrong (the skew window only absorbs about a minute). */
export const CLOCK_WARNING_MS = 120_000;

export function clockLooksWrong(serverOffsetMillis: number): boolean {
  return Math.abs(serverOffsetMillis) > CLOCK_WARNING_MS;
}

export function base32Encode(bytes: Uint8Array): string {
  let bits = 0;
  let value = 0;
  let out = '';
  for (const byte of bytes) {
    value = (value << 8) | byte;
    bits += 8;
    while (bits >= 5) {
      out += CROCKFORD.charAt((value >>> (bits - 5)) & 31);
      bits -= 5;
    }
  }
  if (bits > 0) out += CROCKFORD.charAt((value << (5 - bits)) & 31);
  return out;
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlToBytes(value: string): Uint8Array | null {
  if (!/^[A-Za-z0-9_-]+$/.test(value)) return null;
  const padded = value.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (value.length % 4)) % 4);
  try {
    const binary = atob(padded);
    return Uint8Array.from(binary, (c) => c.charCodeAt(0));
  } catch {
    return null;
  }
}

export function uuidToBase64Url(uuid: string): string {
  const hex = uuid.replace(/-/g, '');
  const bytes = new Uint8Array(16);
  for (let i = 0; i < 16; i += 1) bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return bytesToBase64Url(bytes);
}

export function base64UrlToUuid(value: string): string | null {
  const bytes = base64UrlToBytes(value);
  if (!bytes || bytes.length !== 16) return null;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

async function mac(prefix: TokenPrefix, id: string, secret: Uint8Array, window: number): Promise<string> {
  const key = await globalThis.crypto.subtle.importKey(
    'raw',
    secret,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const signature = await globalThis.crypto.subtle.sign(
    'HMAC',
    key,
    encoder.encode(`${prefix}|${id}|${window}`),
  );
  return base32Encode(new Uint8Array(signature).slice(0, MAC_BYTES));
}

export async function createToken(
  prefix: TokenPrefix,
  id: string,
  secret: Uint8Array,
  window: number,
): Promise<string> {
  return `${prefix}.${uuidToBase64Url(id)}.${window}.${await mac(prefix, id, secret, window)}`;
}

export function parseToken(
  token: string,
): { prefix: string; id: string; window: number; mac: string } | null {
  const parts = token.split('.');
  if (parts.length !== 4) return null;
  const [prefix, idPart, windowPart, macPart] = parts as [string, string, string, string];
  if (!/^\d{1,12}$/.test(windowPart) || !/^[0-9A-HJKMNP-TV-Z]{16}$/.test(macPart)) return null;
  const id = base64UrlToUuid(idPart);
  if (!id || !UUID.test(id)) return null;
  return { prefix, id, window: Number(windowPart), mac: macPart };
}

/** Constant-time string comparison, so a wrong mac takes as long to reject as a nearly right one. */
function safeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i += 1) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

/**
 * INVALID = not ours (bad format, unknown id, wrong prefix or mac). EXPIRED = genuine but outside the skew
 * window, e.g. an old screenshot or a phone clock far off (cashier asks the customer to refresh).
 */
export async function verifyToken(
  token: string,
  expectedPrefix: TokenPrefix,
  options: { secretFor: (id: string) => Promise<Uint8Array | null>; nowWindow: number },
): Promise<VerifyResult> {
  const parsed = parseToken(token);
  if (!parsed || parsed.prefix !== expectedPrefix) return { ok: false, reason: 'INVALID' };
  const secret = await options.secretFor(parsed.id);
  if (!secret) return { ok: false, reason: 'INVALID' };
  const expected = await mac(expectedPrefix, parsed.id, secret, parsed.window);
  if (!safeEqual(expected, parsed.mac)) return { ok: false, reason: 'INVALID' };
  const age = options.nowWindow - parsed.window;
  if (age > SKEW_PAST || age < -SKEW_FUTURE) return { ok: false, reason: 'EXPIRED' };
  return { ok: true, id: parsed.id, window: parsed.window };
}
